import { useRef, useState, useEffect, useCallback, type ReactNode } from "react"
import SearchIcon from "@/shared/icons/totodoicons/stroke/search.svg?react"
import { CircularProgress } from "@/app/components/ui/CircularProgress"
import { useViewContext } from "@/app/stores/viewContext"

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/** Pull distance (px) at which action triggers */
const TRIGGER_THRESHOLD = 80
/** Resistance factor — pull feels heavier than 1:1 */
const RESISTANCE = 3
/** Max visual pull distance */
const MAX_PULL = 120
/** Dead-zone (px) — must pull down this far before PtR claims the gesture.
 *  Prevents accidental activation and lets native scroll win on short swipes. */
const DEAD_ZONE = 15

interface PullToRefreshProps {
  children: ReactNode
  /** Ref to the scroll container (must have overflow-y-auto). */
  scrollRef: React.RefObject<HTMLElement | null>
  /** Called when pull threshold is reached and released. Return a Promise for loading state. */
  onRefresh: () => Promise<void>
}

/**
 * Pull-to-refresh wrapper.
 *
 * ## Critical architecture choice (v1-parity):
 * Uses HEIGHT-based displacement for the pull indicator, NOT CSS transforms.
 * This is essential because `transform: translateY()` on the content wrapper
 * permanently corrupts @dnd-kit's coordinate system for all subsequent drags.
 * By growing a spacer div above the content, the layout flow shifts naturally
 * and dnd-kit's measurements stay correct.
 *
 * Touch-only: uses native touch events (not @use-gesture) to avoid
 * conflicts with @dnd-kit's PointerSensor. Only activates when
 * scrollTop === 0 and pulling down past the dead-zone threshold.
 *
 * ## Gesture state lives in refs, not React state:
 * Event listeners are attached ONCE (stable across renders). All gesture
 * tracking uses refs; only the visual `pullDistance` uses setState — and
 * only via rAF batching so we never re-attach listeners mid-gesture.
 */
export function PullToRefresh({
  children,
  scrollRef,
  onRefresh,
}: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // All gesture state is in refs — never triggers effect re-runs.
  const touchStartY = useRef<number | null>(null)
  /** Whether we've passed the dead-zone and claimed this gesture as PtR */
  const pullClaimed = useRef(false)
  /** Whether the gesture was rejected (user scrolled up first, or went sideways) */
  const gestureRejected = useRef(false)
  const touchStartX = useRef<number | null>(null)
  const currentPullDistance = useRef(0)
  const isRefreshingRef = useRef(false)
  const rafId = useRef(0)

  // Keep ref in sync with state (for use in event handlers)
  isRefreshingRef.current = isRefreshing

  // Stable onRefresh ref to avoid re-attaching listeners when callback changes
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  // Derived state (for rendering only)
  const resistedPull = Math.min(pullDistance / RESISTANCE, MAX_PULL)
  const progress = Math.min(resistedPull / (TRIGGER_THRESHOLD / RESISTANCE), 1)
  const pastThreshold = resistedPull >= TRIGGER_THRESHOLD / RESISTANCE

  // Batched state update — collapses multiple touchmove frames into one render
  const schedulePullUpdate = useCallback((distance: number) => {
    currentPullDistance.current = distance
    cancelAnimationFrame(rafId.current)
    rafId.current = requestAnimationFrame(() => {
      setPullDistance(distance)
    })
  }, [])

  // -------------------------------------------------------------------------
  // Touch handlers — registered ONCE on the scroll container
  // -------------------------------------------------------------------------

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return

    const handleTouchStart = (e: TouchEvent) => {
      // Read drag state imperatively — avoids subscribing to Zustand
      // which would re-render the entire app shell on every drag start/end.
      const { isDragging, dragProtect } = useViewContext.getState()
      if (isDragging || dragProtect || isRefreshingRef.current) return
      if (scrollEl.scrollTop <= 0) {
        touchStartY.current = e.touches[0]!.clientY
        touchStartX.current = e.touches[0]!.clientX
        pullClaimed.current = false
        gestureRejected.current = false
        currentPullDistance.current = 0
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      const { isDragging, dragProtect } = useViewContext.getState()
      if (
        isDragging ||
        dragProtect ||
        isRefreshingRef.current ||
        touchStartY.current === null ||
        gestureRejected.current
      )
        return

      const currentY = e.touches[0]!.clientY
      const currentX = e.touches[0]!.clientX
      const diffY = currentY - touchStartY.current
      const diffX = currentX - (touchStartX.current ?? currentX)

      // If user scrolled content up at all, reject this gesture for PtR
      if (scrollEl.scrollTop > 0) {
        gestureRejected.current = true
        if (pullClaimed.current) {
          pullClaimed.current = false
          schedulePullUpdate(0)
        }
        return
      }

      // Not yet claimed: check dead-zone
      if (!pullClaimed.current) {
        const absY = Math.abs(diffY)
        const absX = Math.abs(diffX)

        // If horizontal movement exceeds vertical, reject (it's a swipe)
        if (absX > absY && absX > 5) {
          gestureRejected.current = true
          return
        }

        // If pulling up, reject (it's a normal scroll)
        if (diffY < 0) {
          gestureRejected.current = true
          return
        }

        // Still inside dead-zone — let native scroll handle it
        if (diffY < DEAD_ZONE) return

        // Past dead-zone, pulling down, scrollTop is 0 — claim the gesture!
        pullClaimed.current = true
      }

      // Gesture is claimed — prevent native scroll and update pull distance
      e.preventDefault()
      if (diffY > 0) {
        schedulePullUpdate(diffY)
      } else {
        // User reversed direction — reset visual but keep gesture claimed
        schedulePullUpdate(0)
      }
    }

    const handleTouchEnd = async () => {
      const { isDragging, dragProtect } = useViewContext.getState()
      if (isDragging || dragProtect || isRefreshingRef.current) {
        schedulePullUpdate(0)
        touchStartY.current = null
        touchStartX.current = null
        pullClaimed.current = false
        gestureRejected.current = false
        return
      }

      const dist = currentPullDistance.current
      const finalResisted = Math.min(dist / RESISTANCE, MAX_PULL)
      const isPastThreshold = finalResisted >= TRIGGER_THRESHOLD / RESISTANCE

      if (isPastThreshold && pullClaimed.current) {
        isRefreshingRef.current = true
        setIsRefreshing(true)
        // Hold at threshold height while refreshing
        cancelAnimationFrame(rafId.current)
        setPullDistance(TRIGGER_THRESHOLD)

        try {
          await onRefreshRef.current()
        } catch (error) {
          console.error("[PullToRefresh] Refresh failed:", error)
        } finally {
          isRefreshingRef.current = false
          setIsRefreshing(false)
          setPullDistance(0)
        }
      } else {
        cancelAnimationFrame(rafId.current)
        setPullDistance(0)
      }

      touchStartY.current = null
      touchStartX.current = null
      pullClaimed.current = false
      gestureRejected.current = false
      currentPullDistance.current = 0
    }

    // Use passive: false on touchmove to allow preventDefault()
    scrollEl.addEventListener("touchstart", handleTouchStart, { passive: true })
    scrollEl.addEventListener("touchmove", handleTouchMove, { passive: false })
    scrollEl.addEventListener("touchend", handleTouchEnd, { passive: true })

    return () => {
      scrollEl.removeEventListener("touchstart", handleTouchStart)
      scrollEl.removeEventListener("touchmove", handleTouchMove)
      scrollEl.removeEventListener("touchend", handleTouchEnd)
      cancelAnimationFrame(rafId.current)
    }
  }, [scrollRef, schedulePullUpdate]) // Stable deps only — no gesture state

  return (
    <div className="relative">
      {/* Pull indicator — HEIGHT-based spacer that pushes content down.
          This avoids transform: translateY which corrupts dnd-kit coordinates. */}
      <div
        className="flex items-center justify-center overflow-hidden"
        style={{
          height: `${resistedPull}px`,
          transition: pullClaimed.current
            ? "none"
            : "height 300ms cubic-bezier(0.22, 1.0, 0.36, 1)",
          minHeight: isRefreshing ? TRIGGER_THRESHOLD / RESISTANCE : 0,
        }}
      >
        {(resistedPull > 0 || isRefreshing) && (
          <div
            className="flex items-center gap-2"
            style={{ opacity: progress }}
          >
            {isRefreshing ? (
              <CircularProgress className="h-5 w-5" />
            ) : (
              <SearchIcon
                className="size-5 transition-colors"
                style={{
                  transform: `scale(${progress}) rotate(${resistedPull * 3}deg)`,
                  color: pastThreshold
                    ? "var(--color-primary)"
                    : "var(--color-muted-foreground)",
                }}
              />
            )}
            <span className="text-xs font-medium text-muted-foreground">
              {isRefreshing ? "Opening…" : "Search"}
            </span>
          </div>
        )}
      </div>

      {/* Actual content — no transform applied */}
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
